import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, CheckCircle2, History } from "lucide-react";
import { formatDateLong } from "@/lib/lotteryLogic";

export const HistoryPanel = ({ workouts, onOpen, onDelete, onSetDraw }) => {
  const [editingId, setEditingId] = useState(null);
  const [drawInput, setDrawInput] = useState("");

  if (!workouts || workouts.length === 0) {
    return (
      <Card className="bg-[#131c2e]/90 border-slate-800">
        <CardContent className="p-8 text-center text-slate-500">
          <History className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p className="text-sm">No saved workouts yet. Save your first run to build history.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-[#131c2e]/90 border-slate-800 shadow-xl shadow-black/40">
      <CardContent className="p-5 sm:p-6 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg sm:text-xl font-bold text-slate-100">
            Saved Workouts
          </h3>
          <span className="text-xs text-slate-500 font-mono-digit">{workouts.length} entries</span>
        </div>

        <div className="divide-y divide-slate-800" data-testid="history-list">
          {workouts.map((w) => {
            const isHit = w.actual_draw && w.starred_hits.includes(w.actual_draw);
            return (
              <div
                key={w.id}
                data-testid={`history-item-${w.id}`}
                className="py-3 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading font-bold text-slate-100">
                      {formatDateLong(w.date)}
                    </span>
                    <span className="text-[10px] uppercase font-heading tracking-widest text-slate-500">
                      {w.mode}
                    </span>
                    <span className="font-mono-digit text-amber-300">seed {w.seed}</span>
                    <span className="font-mono-digit text-emerald-300">sum {w.date_sum}</span>
                    {w.actual_draw && (
                      <span
                        className={`inline-flex items-center gap-1 text-xs font-mono-digit px-2 py-0.5 rounded ${
                          isHit
                            ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                            : "bg-slate-800 text-slate-400"
                        }`}
                      >
                        {isHit && <CheckCircle2 className="w-3 h-3" />}
                        drew {w.actual_draw}
                      </span>
                    )}
                  </div>
                  {w.starred_hits.length > 0 && (
                    <div className="mt-1 text-xs text-slate-400 font-mono-digit truncate">
                      starred: {w.starred_hits.slice(0, 8).join(", ")}
                      {w.starred_hits.length > 8 && "…"}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {editingId === w.id ? (
                    <>
                      <Input
                        data-testid={`draw-input-${w.id}`}
                        value={drawInput}
                        onChange={(e) => setDrawInput(e.target.value.replace(/\D/g, "").slice(0, 4))}
                        placeholder="drawn"
                        className="h-9 w-24 font-mono-digit bg-[#0b0f17] border-slate-700"
                      />
                      <Button
                        size="sm"
                        data-testid={`save-draw-${w.id}`}
                        onClick={() => {
                          onSetDraw(w.id, drawInput);
                          setEditingId(null);
                          setDrawInput("");
                        }}
                        className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 h-9"
                      >
                        Save
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`open-history-${w.id}`}
                        onClick={() => onOpen(w)}
                        className="h-9 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200"
                      >
                        Open
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        data-testid={`mark-draw-${w.id}`}
                        onClick={() => {
                          setEditingId(w.id);
                          setDrawInput(w.actual_draw || "");
                        }}
                        className="h-9 border-slate-700 bg-transparent hover:bg-slate-800 text-slate-200"
                      >
                        Mark hit
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`delete-history-${w.id}`}
                        onClick={() => onDelete(w.id)}
                        className="h-9 text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
